import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from "../../config";
import { getSessionToken } from "../../utils/authSession";

const API_ROOT = config.API_MSWSSTOREMANAGER_BASE_URL;
const REQUEST_TIMEOUT_MS = 30000;

const IMAGE_TYPES = ['primary', 'imgSec1', 'imgSec2', 'imgSec3', 'imgSec4'];

export const SAVE_STEP_LABELS = {
	details: 'Product details',
	pricing: 'Pricing',
	inventory: 'Inventory',
	fulfilment: 'Fulfilment',
	additionalInfo: 'Additional info',
	prefColor: 'Colour',
	prefColorCode: 'Colour code',
	prefSize: 'Size',
	image_primary: 'Primary image',
	image_imgSec1: 'Image 2',
	image_imgSec2: 'Image 3',
	image_imgSec3: 'Image 4',
	image_imgSec4: 'Image 5',
};

class StepError extends Error {
	constructor(message, status) {
		super(message);
		this.status = status;
	}
}

const request = async (url, options) => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
	try {
		const res = await fetch(url, { ...options, signal: controller.signal });
		if (!res.ok) {
			const text = await res.text().catch(() => '');
			let message = '';
			try {
				message = JSON.parse(text)?.message || '';
			} catch {
				message = '';
			}
			throw new StepError(message || `Request failed (${res.status})`, res.status);
		}
		return res;
	} catch (err) {
		if (err.name === 'AbortError') throw new StepError('Request timed out');
		throw err;
	} finally {
		clearTimeout(timer);
	}
};

const postJson = (path, body) =>
	request(`${API_ROOT}${path}`, {
		method: 'POST',
		body: JSON.stringify(body),
		headers: {
			Accept: '*/*',
			'Cache-Control': 'no-cache',
			'Content-Type': 'application/json',
			'X-Session-Token': getSessionToken(),
		},
	});

const uploadImage = (file, imageType, productID, vendorID) => {
	if (!(file instanceof File || file instanceof Blob)) {
		return Promise.resolve();
	}

	const fd = new FormData();
	fd.append('bucketname', 'microstores.net');
	fd.append('uploadfile', file);
	fd.append('productid', productID);
	fd.append('vendorid', vendorID);
	fd.append('imageType', imageType);

	// No Content-Type: fetch sets the multipart boundary itself.
	return request(`${API_ROOT}/v2/uploadProductMediaFile`, {
		method: 'POST',
		body: fd,
		headers: {
			Accept: '*/*',
			'Cache-Control': 'no-cache',
			'X-Session-Token': getSessionToken(),
		},
	});
};

const str = (v) => `${v}`;

const buildSteps = (product) => {
	const { productDetails: pd, pricingDetails, inventoryDetails, fulfillmentDetails, additionalInformation } = product;
	const pricing = pricingDetails.smProductPricing;
	const inventory = inventoryDetails.smProductInventory;
	const fulfilment = fulfillmentDetails.smProductFulfilment;
	const additional = additionalInformation.smProductAdditionalInfo;
	const productId = pd.id;
	const vendorId = pd.vendorId;
	const prefs = product.productPreference.smProductPreference;

	const steps = {
		details: () => postJson('/v2/updateProductDetails', {
			id: str(pd.id),
			vendorId: str(pd.vendorId),
			department: str(pd.department),
			category: str(pd.category),
			subCategory: str(pd.subCategory),
			name: str(pd.name),
			displayName: str(pd.displayName),
			shortDescription: str(pd.shortDescription),
			description: str(pd.description),
			creationDateTime: str(pd.creationDateTime),
			createdBy: str(pd.createdBy),
			updatedDateTime: str(pd.updatedDateTime),
			updatedBy: str(pd.updatedBy),
		}),
		pricing: () => postJson('/v2/updatePricingInfo', {
			productId: str(pricingDetails.productId),
			vendorId: str(pricingDetails.vendorId),
			smProductPricing: {
				id: str(pricing.id),
				barcode: str(pricing.barcode),
				sku: str(pricing.sku),
				totalPrice: str(pricing.totalPrice),
				pricePerUnit: str(pricing.pricePerUnit),
				units: str(pricing.units),
				quantity: str(pricing.quantity),
				contactSupplierFlag: str(pricing.contactSupplierFlag),
				creationDateTime: str(pricing.creationDateTime),
				createdBy: str(pricing.createdBy),
				updatedDateTime: str(pricing.updatedDateTime),
				updatedBy: str(pricing.updatedBy),
			},
		}),
		inventory: () => postJson('/v2/updateProductInventory', {
			productId: str(inventoryDetails.productId),
			vendorId: str(inventoryDetails.vendorId),
			smProductInventory: {
				id: str(inventory.id),
				barcode: str(inventory.barcode),
				sku: str(inventory.sku),
				inStock: str(inventory.inStock),
				lowStock: str(inventory.lowStock),
				lowStockTrack: str(inventory.lowStockTrack),
				maxStock: str(inventory.maxStock),
				creationDateTime: str(inventory.creationDateTime),
				createdBy: str(inventory.createdBy),
				updatedDateTime: str(inventory.updatedDateTime),
				updatedBy: str(inventory.updatedBy),
			},
		}),
		fulfilment: () => postJson('/v2/updateProductFulfilment', {
			productId: str(fulfillmentDetails.productId),
			vendorId: str(fulfillmentDetails.vendorId),
			smProductFulfilment: {
				id: str(fulfilment.id),
				deliveryOption: str(fulfilment.deliveryOption),
				fulfillment: str(fulfilment.fulfillment),
				productStatus: str(fulfilment.productStatus),
				creationDateTime: str(fulfilment.creationDateTime),
				createdBy: str(fulfilment.createdBy),
				updatedDateTime: str(fulfilment.updatedDateTime),
				updatedBy: str(fulfilment.updatedBy),
			},
		}),
		additionalInfo: () => postJson('/v2/updateProductAdditionalInfo', {
			productId: str(additionalInformation.productId),
			vendorId: str(additionalInformation.vendorId),
			smProductAdditionalInfo: {
				id: str(additional.id),
				shippingWeight: str(additional.shippingWeight),
				specialIndicators: str(additional.specialIndicators),
				creationDateTime: str(additional.creationDateTime),
				createdBy: str(additional.createdBy),
				updatedDateTime: str(additional.updatedDateTime),
				updatedBy: str(additional.updatedBy),
			},
		}),
	};

	const prefDefs = [
		['prefColor', 'color', 0],
		['prefColorCode', 'colorCode', 1],
		['prefSize', 'size', 2],
	];
	prefDefs.forEach(([key, type, idx]) => {
		const value = prefs[idx]?.value;
		if (value !== undefined && value !== '') {
			steps[key] = () => postJson(`/v2/preferences/add?vendorId=${vendorId}`, { productId, type, value });
		}
	});

	IMAGE_TYPES.forEach((type) => {
		const file = product.imageFiles?.[type];
		if (file instanceof File || file instanceof Blob) {
			steps[`image_${type}`] = () =>
				uploadImage(file, type, inventoryDetails.productId, inventoryDetails.vendorId);
		}
	});

	return steps;
};

const runStep = async (key, fn) => {
	try {
		await fn();
		return { step: key, ok: true };
	} catch (err) {
		return { step: key, ok: false, message: err?.message || 'Request failed' };
	}
};

/**
 * Saves a product as a set of independent calls and reports each result.
 * Pass `retrySteps` (array of step keys) to run only those steps again.
 * Rejects with { failed: [{step, message}], notAttempted: [step] } if anything fails.
 */
export const storeValue = createAsyncThunk(
	'addProducts/storeValue',
	async (product, { dispatch, rejectWithValue }) => {
		const allSteps = buildSteps(product);
		const retry = Array.isArray(product.retrySteps) ? product.retrySteps : null;
		const wanted = (key) => key in allSteps && (!retry || retry.includes(key));

		const failed = [];
		const notAttempted = [];

		if (wanted('details')) {
			const result = await runStep('details', allSteps.details);
			if (!result.ok) {
				failed.push({ step: 'details', message: result.message });
				Object.keys(allSteps)
					.filter((k) => k !== 'details' && wanted(k))
					.forEach((k) => notAttempted.push(k));
				return rejectWithValue({ failed, notAttempted });
			}
		}

		const rest = Object.keys(allSteps).filter((k) => k !== 'details' && wanted(k));
		const results = await Promise.all(rest.map((k) => runStep(k, allSteps[k])));
		results.filter((r) => !r.ok).forEach((r) => failed.push({ step: r.step, message: r.message }));

		if (failed.length > 0) {
			return rejectWithValue({ failed, notAttempted });
		}

		dispatch(setSaveProductCompleted(true));
		return { saved: true };
	}
);

const initialState = {
	saveProductCompleted: false,
	saveFailures: [],
};

export const AddProductsSlice = createSlice({
	name: 'addProducts',
	initialState,
	reducers: {
		resetSaveProductCompleted: (state) => {
			state.saveProductCompleted = false;
		},
		setSaveProductCompleted: (state, action) => {
			state.saveProductCompleted = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(storeValue.pending, (state) => {
				state.saveFailures = [];
			})
			.addCase(storeValue.fulfilled, (state) => {
				state.saveFailures = [];
			})
			.addCase(storeValue.rejected, (state, action) => {
				const payload = action.payload;
				state.saveFailures = payload?.failed?.length
					? payload.failed
					: [{ step: 'details', message: action.error?.message || 'Save failed' }];
			});
	},
});

export const { resetSaveProductCompleted, setSaveProductCompleted } = AddProductsSlice.actions;
export const SelectSaveProductCompleted = (state) => state.addProducts.saveProductCompleted;
export const SelectSaveFailures = (state) => state.addProducts.saveFailures;
export default AddProductsSlice.reducer;
export const saveProductThunk = storeValue;
